/**
 * Toast - Neumorphic toast notification with theme support
 */

import { Toast as ToastType, useToastStore } from '../../store/toastStore';
import { useTheme, Icons } from '../../lib/design-system';

interface ToastProps {
  toast: ToastType;
}

export default function Toast({ toast }: ToastProps) {
  const { theme } = useTheme();
  const { removeToast } = useToastStore();

  // MOJ-140: the toast floats over arbitrary page content (lists, photos), so
  // its background must be fully opaque. The accent tint is painted as a
  // gradient layer ON TOP of the opaque theme surface instead of being a
  // semi-transparent fill, and the message uses the theme's regular text
  // colour for contrast in light and dark themes; the type colour is carried
  // by the left border and the icon chip only.
  const getStyles = () => {
    switch (toast.type) {
      case 'success':
        return {
          tint: theme.success,
          icon: Icons.Check,
          iconBg: theme.successLight,
          iconColor: theme.success,
        };
      case 'error':
        return {
          tint: theme.error,
          icon: Icons.Close,
          iconBg: theme.errorLight,
          iconColor: theme.error,
        };
      case 'info':
        return {
          tint: theme.info,
          icon: Icons.Info,
          iconBg: theme.infoLight,
          iconColor: theme.info,
        };
      case 'warning':
        return {
          tint: theme.warning,
          icon: Icons.Warning,
          iconBg: theme.warningLight,
          iconColor: theme.warning,
        };
      default:
        return {
          tint: theme.border,
          icon: Icons.Info,
          iconBg: theme.background,
          iconColor: theme.textMuted,
        };
    }
  };

  const styles = getStyles();
  const IconComponent = styles.icon;
  // `${hex}14` = 8 % alpha tint; it only ever composites over the opaque surface below it.
  const tintLayer = `linear-gradient(${styles.tint}14, ${styles.tint}14)`;

  return (
    <div
      role="alert"
      aria-live="polite"
      data-testid={`toast-${toast.type}`}
      className="flex items-center gap-3 px-4 py-3 rounded-xl animate-slide-in"
      style={{
        backgroundColor: theme.surface,
        backgroundImage: tintLayer,
        border: `1px solid ${theme.border}`,
        borderLeft: `4px solid ${styles.tint}`,
        boxShadow: theme.shadowMd,
        color: theme.text,
      }}
    >
      <span
        className="flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center"
        style={{ backgroundColor: styles.iconBg }}
      >
        <IconComponent size={16} weight="bold" style={{ color: styles.iconColor }} />
      </span>
      <span className="flex-1 text-sm font-medium" style={{ color: theme.text }}>
        {toast.message}
      </span>
      <button
        onClick={() => removeToast(toast.id)}
        className="flex-shrink-0 p-1.5 rounded-lg transition-all hover:shadow-neu-sm"
        style={{ backgroundColor: styles.iconBg, color: styles.iconColor }}
        aria-label="Benachrichtigung schließen"
      >
        <Icons.Close size={14} weight="bold" />
      </button>
    </div>
  );
}
